/* ==========================================================================
   Bluefin A/B — swap for galleries that arrive after page load.

   The collection pages carry quick-view buttons. The modal they open renders
   a full product gallery through the Section Rendering API and inserts it
   with innerHTML — and inline <script> tags inserted that way NEVER execute.
   So the copy of the swap inside ab-test-gallery.liquid, which handles the
   product page before its first paint, simply does not run there.

   Result before this file existed: an arm-b visitor opened a quick-view and
   saw arm A's photographs, no exposure was recorded, and anything they bought
   from that modal was still tagged as arm b. Shown A, counted as B — which
   does not invent a winner, it quietly drags any real difference toward zero.

   This watches for payload blocks appearing in the DOM and swaps them the
   moment they do. Post-paint is fine here: the modal is already an
   interaction, not a page load.
   ========================================================================== */
(function () {
  'use strict';

  var AB = window.BluefinAB;
  if (!AB) return;                 /* no test running */

  function swap(el) {
    try {
      if (!el || el.getAttribute('data-bf-done')) return;
      el.setAttribute('data-bf-done', '1');

      var data;
      try { data = JSON.parse(el.textContent); } catch (e) { return; }
      if (!data || !data.a || !data.b) return;

      /* Same rule as ab-test-gallery.liquid, and it has to be the same or the
         two paths disagree about who is in the test. Not testable -> excluded
         from both arms, and say so rather than returning in silence. */
      if (data.a.length !== data.b.length) {
        console.warn('[bluefin-ab] gallery B has ' + data.b.length +
                     ' images against ' + data.a.length +
                     ' in A. Product excluded from the test.');
        document.documentElement.setAttribute('data-ab-invalid', 'length-mismatch');
        return;
      }

      /* A file deleted from Shopify Files can leave a dangling reference, and
         `image_url` then renders empty. Unchecked, paint() builds "?width=800",
         arm B requests the page's own HTML as an image and shows broken
         pictures -- arm A intact, counters running, and a confident "B is much
         worse" that is entirely an artefact. Route it into the behaviour that
         is already correct: not testable, so excluded from both arms. */
      for (var k = 0; k < data.b.length; k++) {
        if (!data.b[k] || !data.b[k].u) {
          console.warn('[bluefin-ab] gallery B image ' + (k + 1) + ' has no URL ' +
                       '(file deleted?). Product excluded from the test.');
          document.documentElement.setAttribute('data-ab-invalid', 'missing-image');
          return;
        }
      }

      AB.inTest = true;
      if (data.product) AB.product = data.product;

      var root = (el.closest && el.closest('.product')) || document;
      var isB = document.documentElement.getAttribute('data-ab-arm') === 'b';

      /* Kept identical to the painter in ab-test-gallery.liquid. Each element
         keeps its own width ladder; imposing one ladder on all of them was a
         real confound on mobile and is not worth reintroducing here for the
         sake of a shorter function. */
      function paint(img, b) {
        var widths = [];
        (img.getAttribute('srcset') || '').split(',').forEach(function (part) {
          var m = part.trim().match(/\s(\d+)w$/);
          if (m) {
            var w = parseInt(m[1], 10);
            if (w <= b.w) widths.push(w);
          }
        });
        if (!widths.length) widths = [Math.min(b.w, 800)];

        var sep = b.u.indexOf('?') === -1 ? '?' : '&';
        var at = function (w) { return b.u + sep + 'width=' + w; };

        var cur = (img.getAttribute('src') || '').match(/[?&]width=(\d+)/);
        var sw = cur ? Math.min(parseInt(cur[1], 10), b.w) : Math.min(b.w, 1200);

        img.removeAttribute('data-srcset');
        img.removeAttribute('data-src');
        img.setAttribute('srcset', widths.map(function (w) {
          return at(w) + ' ' + w + 'w';
        }).join(', '));
        img.setAttribute('src', at(sw));
        if (b.alt) img.setAttribute('alt', b.alt);
      }

      /* Same rule as ab-test-gallery.liquid: the targets are looked for in
         BOTH arms, before the arm matters, and a view only counts as an
         exposure once they are found. See the note there -- deciding :noswap
         in arm B alone made the exclusion one-sided. */
      function findTargets() {
        return data.a.map(function (mediaId) {
          return root.querySelectorAll('[data-media-id="' + mediaId + '"] img');
        });
      }
      function total(targets) {
        var n = 0;
        targets.forEach(function (list) { n += list.length; });
        return n;
      }

      /* Seeing a test gallery is the exposure, wherever it appears. This is
         the one that arrives late, so markExposed also promotes the visitor to
         "seen" and re-tags the forms already in the modal. */
      function finish(targets) {
        var mediaHit = 0;
        targets.forEach(function (list) { if (list.length) mediaHit++; });
        if (AB.markExposed) AB.markExposed();
        if (mediaHit < data.a.length) {
          console.warn('[bluefin-ab] only ' + mediaHit + ' of ' + data.a.length +
                       ' media matched the payload. Not excluded, but check the markup.');
          document.documentElement.setAttribute('data-ab-partial',
                                                mediaHit + '/' + data.a.length);
        }
        if (!isB) return;
        var swapped = 0;
        targets.forEach(function (list, i) {
          var b = data.b[i];
          if (!b) return;
          list.forEach(function (img) {
            paint(img, b);
            swapped++;
          });
        });
        paintStickyWhenReady();
        AB.swappedLate = (AB.swappedLate || 0) + swapped;
      }

      var targets = findTargets();
      if (total(targets) === 0) {
        /* Unlike the product page, here the markup can genuinely arrive a beat
           after the payload -- a modal that inserts a skeleton first and fills
           it in a second pass. Flagging immediately would void good data, so
           retry once: the retry repairs the timing case AND only the genuine
           failures survive to be flagged. Nothing is counted in the meantime,
           in either arm. */
        setTimeout(function () {
          try {
            /* If the shopper closed the quick-view in the meantime, root is
               detached and can never match anything again. That is not a swap
               failure and must not be flagged -- nor counted as a view. */
            if (!root || root.isConnected === false) return;
            var again = findTargets();
            if (total(again) > 0) {
              finish(again);
              return;
            }
            console.warn('[bluefin-ab] no gallery image matched the payload, twice. ' +
                         'Visitor flagged :noswap and excluded from the analysis.');
            document.documentElement.setAttribute('data-ab-noswap', '1');
            AB.swapFailed = true;
            if (AB.markSwapFailed) AB.markSwapFailed();
          } catch (e) {}
        }, 400);
        return;
      }
      finish(targets);

      /* Same sticky-bar rule as the inline copy, and scoped the same way. A
         quick-view has no sticky buy bar of its own, so this is normally a
         no-op here -- it is kept so the two paths cannot drift apart, which
         is how the length-mismatch handling went wrong before. */
      /* DEFERRED on purpose. This runs mid-parse and the sticky bar is further
         down the document, so it does not exist yet -- the first version of
         this fix silently swapped nothing. It is below the fold and only
         appears on scroll, so waiting for the parse to finish costs nothing. */
      function paintSticky() {
        var sib = root.parentElement;
        var first = data.b[0];
        if (!sib || !first) return;
        var n = 0;
        sib.querySelectorAll('[data-sticky-product-media] img').forEach(function (img) {
          paint(img, first);
          n++;
        });
        if (AB) AB.swappedSticky = (AB.swappedSticky || 0) + n;
      }
      function paintStickyWhenReady() {
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', paintSticky);
        } else {
          paintSticky();
        }
      }
    } catch (e) {}
  }

  function scan(node) {
    try {
      if (node.nodeType !== 1) return;
      if (node.matches && node.matches('script.bf-ab-payload')) { swap(node); return; }
      if (node.querySelectorAll) {
        node.querySelectorAll('script.bf-ab-payload:not([data-bf-done])').forEach(swap);
      }
    } catch (e) {}
  }

  if (window.MutationObserver) {
    try {
      new MutationObserver(function (records) {
        for (var i = 0; i < records.length; i++) {
          var added = records[i].addedNodes;
          for (var j = 0; j < added.length; j++) scan(added[j]);
        }
      }).observe(document.documentElement, { childList: true, subtree: true });
    } catch (e) {}
  }

  /* Anything already present and unhandled when this file loads — for
     instance if the deferred asset lost the race with a fast modal. */
  try {
    document.querySelectorAll('script.bf-ab-payload:not([data-bf-done])').forEach(swap);
  } catch (e) {}
})();